<template>
  <el-upload
    v-loading="loading"
    class="image-uploader"
    :disabled="disabled"
    :style="computedStyle"
    :action="uploadUrl"
    :show-file-list="false"
    :on-success="handleUploadSuccess"
    :on-error="handleUploadError"
    :before-upload="beforeUpload"
  >
    <img
      v-if="previewUrl && needTransparentBackground"
      src="./images/transparent-background.png"
      class="transparent-background"
      alt=""
    />
    <div
      v-if="previewUrl"
      v-qm-background="{mode: mode, url: previewUrl}"
      class="image-uploader-preview"
      :class="{disabled: disabled}"
    >
      <qm-button
        v-if="!disabled && computedShowUploadIcon"
        icon="image-upload"
        style="margin-right: 12px"
      />
      <qm-button
        v-if="!disabled"
        icon="question-delete"
        @click.native.stop="handleClickRemoveImage"
      />
    </div>
    <i v-else class="el-icon-plus image-uploader-icon" />
  </el-upload>
</template>

<script>
/* eslint-disable */
import * as qm from 'src/utils/utils'
/* eslint-enable */
/**
 * 通用的 image uploader, 只适用于上传单张图片
 * @param [width] {Number|String} 上传框宽度，默认 178
 * @param [height] {Number|String} 上传框的高度，默认 178
 * @param url {String} 图片实际地址，可用 .sync 修饰符
 * @param [imageWidth] {Number} 图片实际宽度，可用 .sync 修饰符
 * @param [imageHeight] {Number} 图片实际高度，可以 .sync 修饰符
 * @param [mode = 'aspectFit'] {String} 预览框内图片展示的样式 values: ['scaleToFill', 'aspectFit', 'aspectFill', 'widthFix']
 * @param [disabled = 'false'] {Boolean} 是否不可用
 * @param [needTransparentBackground] {Boolean} 是否有透明背景
 * @event remove 图片被删除
 * @event upload-succeed({url,height,width}) 图片上传成功
 * @event upload-start 图片开始上传
 * @event upload-failed
 */
export default {
  name: 'QmImageUploader',
  props: {
    url: {
      type: String,
      default: () => '',
    },
    imageHeight: {
      type: Number,
      default: () => 0,
    },
    imageWidth: {
      type: Number,
      default: () => 0,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    width: {
      type: [String, Number],
      default: 178,
    },
    height: {
      type: [String, Number],
      default: 178,
    },
    needTransparentBackground: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'aspectFit',
      validator: function(value) {
        return ['scaleToFill', 'aspectFit', 'aspectFill', 'widthFix'].includes(
          value,
        )
      },
    },
  },
  data() {
    return {
      imageBaseUrl: 'http://statics01.readland.cn/',
      uploadUrl: '',
      loading: false,
      previewUrl: '',
      naturalHeight: '',
      naturalWidth: '',
    }
  },
  computed: {
    computedStyle() {
      switch (this.mode) {
        case 'scaleToFill': // 固定宽高内，拉伸覆盖
          return {
            width: this.width + 'px',
            height: this.height + 'px',
            backgroundSize: 'auto',
            backgroundPosition: 'center',
          }
        case 'aspectFit': // 在固定的宽高，可以留白，展示全部
          return {
            width: this.width + 'px',
            height: this.height + 'px',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          }
        case 'aspectFill': // 在固定的宽高内，不留白，填满
          return {
            width: this.width + 'px',
            height: this.height + 'px',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        case 'widthFix': // 宽度固定，高度根据图片大小来展示
          return {
            width: this.width + 'px',
            height: this.naturalHeight
              ? (this.naturalHeight / this.naturalWidth) * this.width + 'px'
              : this.height + 'px',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          }
      }
      return {
        width: this.width + 'px',
        height: this.height + 'px',
      }
    },
    computedShowUploadIcon() {
      let height = Number(this.height)
      let width = Number(this.width)
      const min = Math.min(height, width)
      return min > 120
    },
  },
  watch: {
    url(nv) {
      this.previewUrl = nv
    },
    imageHeight(nv) {
      this.naturalHeight = nv
    },
    imageWidth(nv) {
      this.naturalWidth = nv
    },
  },
  created() {
    const token = qm.getUserToken()
    this.uploadUrl = `http://api.readland.cn/v1/quiz.image.upload?token=${token}`
    this.naturalHeight = this.imageHeight
    this.naturalWidth = this.imageWidth
  },
  mounted() {
    this.previewUrl = this.url
  },
  methods: {
    beforeUpload(file) {
      qm.log('beforeUpload', file.type)
      const isType =
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/gif'
      const isLt2M = file.size / 1024 / 1024 < 11
      if (!isType) {
        this.$message.error('只支持 JPG / PNG / GIF 格式')
      }
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 11MB')
      }
      let img = document.createElement('img')
      img.onload = () => {
        qm.log('onload\n img :\n', img)
        this.naturalHeight = img.naturalHeight
        this.naturalWidth = img.naturalWidth
      }
      img.src = URL.createObjectURL(file)
      qm.log('beforeUpload\n img :\n', img)
      if (isType && isLt2M) {
        this.$emit('upload-start')
        this.loading = true
        return true
      } else {
        return false
      }
    },
    handleUploadSuccess(res, file) {
      if (res.ok) {
        this.$emit('update:url', this.imageBaseUrl + res.key)
        // 触发一次 upload-success 事件，以便触发外边的检查
        this.$emit('upload-succeed', {
          height: this.naturalHeight,
          width: this.naturalWidth,
          url: this.imageBaseUrl + res.key,
        })
        this.previewUrl = URL.createObjectURL(file.raw)
        qm.log('handleUploadSuccess\n file :\n', file)
      } else {
        this.$message.error('上传失败，请重试')
      }
      this.loading = false
    },
    handleUploadError(error) {
      this.$message.error('上传失败，请重试')
      this.$emit('upload-failed')
      this.loading = false
    },
    handleClickRemoveImage() {
      this.naturalWidth = ''
      this.naturalHeight = ''
      this.$emit('remove')
    },
  },
}
</script>

<style lang="scss"></style>
